import React, { useState, useEffect } from "react";
import { Droplets, Sparkles, Waves, Shirt, MapPin } from "lucide-react";
import { Card, Input, Toggle, Textarea, PillGroup, TagGroup, Stepper, PlaceSearch, RepeatableGroup } from "./FormComponents";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet marker icons
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

if (typeof document !== 'undefined') {
    const styleId = 'map-filters-style';
    if (!document.getElementById(styleId)) {
        const s = document.createElement('style');
        s.id = styleId;
        s.textContent = `
            .dark-map-tiles .leaflet-tile-container { filter: brightness(1.6) contrast(1.2); }
            .light-map-tiles .leaflet-tile-container { filter: grayscale(1) contrast(2.5) brightness(1.1); }
        `;
        document.head.appendChild(s);
    }
}

const MapViewUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
            if (center[0] && center[1]) map.flyTo(center, 17, { duration: 1.5 });
        }, 100);
    }, [center, map]);
    return null;
};

export const Step11 = ({ data, updateData, isLight, showValidation, propertyLocation }) => {
  const [bathroomErrors, setBathroomErrors] = useState({});

  // Haversine distance in meters between two lat/lon points
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const getWalkingTime = (meters) => {
    const minutes = Math.round(meters / 80); // ~80m/min walking pace
    if (minutes < 1) return 'Less than 1 min walk';
    if (minutes === 1) return '1 min walk';
    return `${minutes} min walk`;
  };

  const handleChange = (field, value) => {
    updateData("bathroomLaundry", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("bathroomLaundry", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleServiceUpdate = (index, field, value) => {
    const currentServices = data.laundry.nearbyServices?.services || [];
    const newServices = [...currentServices];
    newServices[index] = { ...newServices[index], [field]: value };
    handleNestedChange("laundry", "nearbyServices", { 
      ...(data.laundry.nearbyServices || { available: false }), 
      services: newServices 
    });
  };

  const nearbyServices = data.laundry.nearbyServices || { available: false, services: [] };

  const handleBathroomCountChange = (val) => {
    const count = val === "" ? 0 : val;
    let newBathrooms = [...data.bathrooms];
    if (newBathrooms.length < count) {
      for (let i = newBathrooms.length; i < count; i++) {
        newBathrooms.push({
          id: (Date.now() + i).toString(),
          type: [],
          privacy: ""
        });
      }
    } else if (newBathrooms.length > count) {
      newBathrooms = newBathrooms.slice(0, count);
    }
    updateData("bathroomLaundry", { ...data, bathroomCount: val, bathrooms: newBathrooms });
  };

  const handleBathroomUpdate = (index, field, value) => {
    const newBathrooms = [...data.bathrooms];
    newBathrooms[index] = { ...newBathrooms[index], [field]: value };
    handleChange("bathrooms", newBathrooms);
  };

  const handleTypeChange = (index, val) => {
    const bathroom = data.bathrooms[index];
    const hadWC = bathroom.type.includes("WC");
    const hasWC = val.includes("WC");

    setBathroomErrors(prev => {
      const next = { ...prev };
      delete next[bathroom.id];
      return next;
    });

    if (hasWC && val.length > 1) {
      if (!hadWC) {
        handleBathroomUpdate(index, "type", ["WC"]);
        setBathroomErrors(prev => ({ 
          ...prev, 
          [bathroom.id]: "WC cannot be combined with other facilities." 
        }));
      } else {
        setBathroomErrors(prev => ({ 
          ...prev, 
          [bathroom.id]: "Cannot combine Individual WC with Shower/Bathtub." 
        }));
      }
      return;
    }
    
    handleBathroomUpdate(index, "type", val);
  };

  const amenitiesOptions = ["Towels", "Hand towels", "Toilet paper", "Soap", "Hand soap", "Shower gel / body wash", "Shampoo", "Hair dryer", "Bidet", "Slippers", "Bathrobes", "Whirlpool bathtub", "Other"];

  return (
    <div className="flex flex-col gap-8">
      <Card title="Set up the bathroom layout and privacy." icon={Droplets} subtitle="Bathroom setup, toiletries, and laundry facilities." isLight={isLight}>
        <div className="flex flex-col gap-10">
          <div className="max-w-xs">
            <Stepper 
              label="Number of Bathrooms" required 
              value={data.bathroomCount} isLight={isLight}
              showValidation={showValidation}
              onChange={handleBathroomCountChange}
            />
          </div>

          <div className="flex flex-col gap-12">
            {data.bathrooms.map((bathroom, index) => (
              <div key={bathroom.id} className={`pt-10 first:pt-0 border-t first:border-t-0 ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-xl font-bold ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>Bathroom {index + 1}</h3>
                    <div className={`h-px flex-1 mx-6 ${isLight ? 'bg-neutral-100' : 'bg-white/5'}`} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <TagGroup 
                      label="Bathroom type" required options={["Shower", "Bathtub", "WC"]} 
                      value={bathroom.type} isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleTypeChange(index, val)}
                      error={bathroomErrors[bathroom.id]}
                    />
                    <PillGroup 
                      label="Privacy" required options={["Private", "Shared"]} 
                      value={bathroom.privacy} isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleBathroomUpdate(index, "privacy", val)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card title="Bathroom Amenities" icon={Sparkles} subtitle="Select the bathroom items provided for guests." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <TagGroup 
              label="Provided" options={amenitiesOptions} 
              value={data.amenities} isLight={isLight} 
              showValidation={showValidation}
              onChange={(val) => {
                const updates = { amenities: val };
                if (!val.includes("Other")) updates.otherAmenities = "";
                updateData("bathroomLaundry", { ...data, ...updates });
              }} 
            />
            {data.amenities.includes("Other") && (
              <Input 
                label="Specify other amenities" placeholder="e.g., Shower cap, Mouthwash..." required
                value={data.otherAmenities} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleChange("otherAmenities", e.target.value)}
              />
            )}
          </div>
        </div>
      </Card>

      <Card title="Plumbing Rules" icon={Waves} subtitle="Toilet disposal rules guests should know." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Should toilet paper and wipes be thrown in the bathroom bin, not in the toilet?" 
            value={data.plumbing.useBinOnly} isLight={isLight}
            onChange={(val) => handleNestedChange("plumbing", "useBinOnly", val)}
          />
        </div>
      </Card>

      <Card title="Laundry" icon={Shirt} subtitle="Washing and ironing details for the property." isLight={isLight}>
        <div className="flex flex-col gap-10">
          <Toggle 
            label="Washing machine available?" 
            value={data.laundry.washingMachine.available} isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) updates.instructions = "";
              updateData("bathroomLaundry", {
                ...data,
                laundry: { 
                  ...data.laundry, 
                  washingMachine: { ...data.laundry.washingMachine, ...updates } 
                }
              });
            }}
          >
            <Textarea 
              label="Instructions" placeholder="Use the 'Quick' cycle for daily laundry. Detergent is provided." 
              value={data.laundry.washingMachine.instructions} isLight={isLight}
              onChange={(e) => handleNestedChange("laundry", "washingMachine", { ...data.laundry.washingMachine, instructions: e.target.value })}
            />
          </Toggle>

          <Toggle 
            label="Clothes dryer available?" 
            value={data.laundry.dryer.available} isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) updates.instructions = "";
              updateData("bathroomLaundry", {
                ...data,
                laundry: { 
                  ...data.laundry, 
                  dryer: { ...data.laundry.dryer, ...updates } 
                }
              });
            }}
          >
            <Textarea 
              label="Instructions" placeholder="Select 'Cotton' and press start..." 
              value={data.laundry.dryer.instructions} isLight={isLight}
              onChange={(e) => handleNestedChange("laundry", "dryer", { ...data.laundry.dryer, instructions: e.target.value })}
            />
          </Toggle>

          <Toggle 
            label="Drying rack available?" 
            value={data.laundry.dryingRack} isLight={isLight}
            onChange={(val) => handleChange("laundry", { ...data.laundry, dryingRack: val })}
          />

          <Toggle 
            label="Iron available?" 
            value={data.laundry.iron.available} isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) updates.location = "";
              updateData("bathroomLaundry", {
                ...data,
                laundry: { 
                  ...data.laundry, 
                  iron: { ...data.laundry.iron, ...updates } 
                }
              });
            }}
          >
            <Input 
              label="Iron location" placeholder="In the bedroom closet" required
              value={data.laundry.iron.location} isLight={isLight}
              showValidation={showValidation}
              onChange={(e) => handleNestedChange("laundry", "iron", { ...data.laundry.iron, location: e.target.value })}
            />
          </Toggle>

          <Toggle 
            label="Ironing board available?" 
            value={data.laundry.ironingBoard.available} isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) updates.location = "";
              updateData("bathroomLaundry", {
                ...data,
                laundry: { 
                  ...data.laundry, 
                  ironingBoard: { ...data.laundry.ironingBoard, ...updates } 
                }
              });
            }}
          >
            <Input 
              label="Ironing board location" placeholder="Behind the bedroom door" required
              value={data.laundry.ironingBoard.location} isLight={isLight}
              showValidation={showValidation}
              onChange={(e) => handleNestedChange("laundry", "ironingBoard", { ...data.laundry.ironingBoard, location: e.target.value })}
            />
          </Toggle>
        </div>
      </Card>

      <Card title="Laundry nearby locations" icon={MapPin} subtitle="Nearby laundry services guests can use" isLight={isLight}>
        <Toggle 
          label="Is there laundry service nearby?" 
          value={nearbyServices.available} isLight={isLight}
          onChange={(val) => {
            const updates = { available: val };
            if (!val) updates.services = [];
            updateData("bathroomLaundry", {
              ...data,
              laundry: { 
                ...data.laundry, 
                nearbyServices: { ...nearbyServices, ...updates } 
              }
            });
          }}
        >
          <RepeatableGroup
            label="Services"
            items={nearbyServices.services}
            isLight={isLight}
            required={true}
            showValidation={showValidation}
            onAdd={() => {
              const newServices = [...nearbyServices.services, { id: Date.now().toString(), name: "", address: "", hours: "", lat: null, lon: null, distance: "", notes: "" }];
              handleNestedChange("laundry", "nearbyServices", { ...nearbyServices, services: newServices });
            }}
            onRemove={(index) => {
              const newServices = nearbyServices.services.filter((_, i) => i !== index);
              handleNestedChange("laundry", "nearbyServices", { ...nearbyServices, services: newServices });
            }}
            renderItem={(service, index) => (
              <div className="flex flex-col gap-6">
                <PlaceSearch
                  label="Search for laundry service"
                  value={service.name}
                  placeholder="Search by name or address..."
                  locationBias={propertyLocation}
                  isLight={isLight}
                  onSelect={(place) => {
                    let distance = '';
                    if (propertyLocation?.lat && propertyLocation?.lon && place.lat && place.lon) {
                      const meters = haversineDistance(propertyLocation.lat, propertyLocation.lon, place.lat, place.lon);
                      distance = getWalkingTime(meters);
                    }
                    const newServices = [...nearbyServices.services];
                    newServices[index] = {
                      ...newServices[index],
                      name: place.name,
                      address: place.address,
                      lat: place.lat,
                      lon: place.lon,
                      hours: place.hours || '',
                      distance,
                    };
                    handleNestedChange("laundry", "nearbyServices", { ...nearbyServices, services: newServices });
                  }}
                />

                <div className={`h-56 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                  <MapContainer
                    center={[service.lat || 37.9838, service.lon || 23.7275]}
                    zoom={service.lat ? 17 : 13}
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%' }}
                    className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                  >
                    {isLight ? (
                      <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        minZoom={0} maxZoom={20}
                      />
                    ) : (
                      <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        minZoom={0} maxZoom={20}
                      />
                    )}
                    {service.lat && service.lon && (
                      <Marker position={[service.lat, service.lon]} />
                    )}
                    <MapViewUpdater center={[service.lat || 37.9838, service.lon || 23.7275]} />
                  </MapContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input 
                    label="Service Name" placeholder="QuickClean Laundry..." required
                    value={service.name} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleServiceUpdate(index, "name", e.target.value)}
                  />
                  <Input 
                    label="Address" placeholder="123 Main St..." required
                    value={service.address} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleServiceUpdate(index, "address", e.target.value)}
                  />
                  <Input 
                    label="Business Hours" placeholder="Mon-Fri: 08:00-20:00"
                    value={service.hours || ''} isLight={isLight}
                    onChange={(e) => handleServiceUpdate(index, "hours", e.target.value)}
                  />
                  <div className="flex flex-col gap-1">
                    <Input 
                      label="Walking Distance" placeholder="e.g. 5 min walk"
                      value={service.distance} isLight={isLight}
                      onChange={(e) => handleServiceUpdate(index, "distance", e.target.value)}
                    />
                    {service.distance && (
                      <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        Auto-calculated from property location
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Textarea
                      label="Instructions / Notes"
                      placeholder="Walk 2 blocks north, turn left at the pharmacy. Ring the bell if the door is closed."
                      value={service.notes || ''} isLight={isLight}
                      onChange={(e) => handleServiceUpdate(index, "notes", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          />
        </Toggle>
      </Card>
    </div>
  );
};
